import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getMyCompletedDeliveries,
    subscribeToMyDeliveryUpdates,
    isDeliveryCompleted
} from "../../lib/supabase/my-deliveries";

import {
    FiCalendar, FiUser, FiPhone, FiMapPin, FiAlertCircle, FiEye,
    FiMail, FiPhoneOff, FiClock, FiCheckCircle, FiTruck,
    FiFlag, FiSearch, FiPackage, FiHome, FiLayers, FiBox, FiEdit3, FiInfo
} from "react-icons/fi";

const MyCompletedDeliveries = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    const [deliveries, setDeliveries] = useState([]);
    const [filteredDeliveries, setFilteredDeliveries] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        if (company?.id && userProfile?.id) {
            fetchCompletedDeliveries();
        }
    }, [company, userProfile]);

    const fetchCompletedDeliveries = async () => {
        try {
            const data = await getMyCompletedDeliveries(userProfile.id, company.id);
            setDeliveries(data);
            setFilteredDeliveries(data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching completed deliveries:", error);
            alert("Error fetching your completed deliveries");
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToMyDeliveryUpdates(company.id, (payload) => {
            fetchCompletedDeliveries();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id]);

    // Filter and search logic
    useEffect(() => {
        let filtered = [...deliveries];

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (delivery) =>
                    delivery.unique_id?.toLowerCase().includes(term) ||
                    delivery.quote_info?.childUniqueId?.toLowerCase().includes(term) ||
                    delivery.customer_name?.toLowerCase().includes(term) ||
                    delivery.customer_address?.toLowerCase().includes(term)
            );
        }

        // Sort
        switch (sortBy) {
            case "newest":
                filtered.sort((a, b) => new Date(b.delivery?.delivered_at || b.created_at) - new Date(a.delivery?.delivered_at || a.created_at));
                break;
            case "oldest":
                filtered.sort((a, b) => new Date(a.delivery?.delivered_at || a.created_at) - new Date(b.delivery?.delivered_at || b.created_at));
                break;
            case "delivery_date":
                filtered.sort((a, b) => {
                    const aDate = a.quote_info?.expected_delivery_date || a.created_at;
                    const bDate = b.quote_info?.expected_delivery_date || b.created_at;
                    return new Date(aDate) - new Date(bDate);
                });
                break;
            case "completed_date":
                filtered.sort((a, b) => {
                    const aDate = a.delivery?.delivered_at || a.created_at;
                    const bDate = b.delivery?.delivered_at || b.created_at;
                    return new Date(bDate) - new Date(aDate); // Most recent first
                });
                break;
            default:
                break;
        }

        setFilteredDeliveries(filtered);
    }, [deliveries, searchTerm, sortBy]);

    const getDeliveryStatusColor = (status) => {
        const statusColors = {
            pending: "bg-amber-50 text-amber-700 border-amber-200",
            in_transit: "bg-blue-50 text-blue-700 border-blue-200",
            delivered: "bg-green-50 text-green-700 border-green-200",
            completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
            cancelled: "bg-red-50 text-red-700 border-red-200"
        };
        return statusColors[status] || "bg-gray-50 text-gray-700 border-gray-200";
    };

    const getDeliveryStatusIcon = (status) => {
        const icons = {
            pending: <FiClock className="w-3.5 h-3.5" />,
            in_transit: <FiTruck className="w-3.5 h-3.5" />,
            delivered: <FiCheckCircle className="w-3.5 h-3.5" />,
            completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            cancelled: <FiAlertCircle className="w-3.5 h-3.5" />
        };
        return icons[status] || <FiClock className="w-3.5 h-3.5" />;
    };

    const getQuoteTypeIcon = (quoteType) => {
        const icons = {
            roof: <FiHome className="w-3 h-3" />,
            wall: <FiLayers className="w-3 h-3" />,
            floor: <FiBox className="w-3 h-3" />,
            timber: <FiPackage className="w-3 h-3" />
        };
        return icons[quoteType] || <FiPackage className="w-3 h-3" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatRelativeTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);
        const diffMs = now - inputDate;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'just now';
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return formatDate(date);
    };

    const handleViewDelivery = (jobId) => {
        navigate(`/${companySlug}/assignment/${jobId}`, {
            state: { from: location.pathname }
        });
    };

    const FilterButton = ({ label, isSelected, onClick }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-lg border transition-all duration-200 font-medium text-sm ${isSelected
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
        >
            {label}
        </button>
    );

    // Get quote type styling (color and badge)
    const getQuoteTypeStyle = (quoteType) => {
        switch (quoteType) {
            case 'roof':
                return { bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' };
            case 'wall':
                return { bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' };
            case 'floor':
                return { bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-200' };
            case 'timber':
                return { bgColor: 'bg-amber-100', textColor: 'text-amber-800', borderColor: 'border-amber-200' };
            default:
                return { bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-200' };
        }
    };

    const isQuoteDeliveryCompleted = (quoteCard) => {
        return isDeliveryCompleted(quoteCard.delivery);
    };

    return (
        <div className="mobile-list-page driver-deliveries-page min-h-screen">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FiCheckCircle className="w-7 h-7 text-green-600" />
                                Completed Deliveries
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">
                                Viewing your delivery history • Last updated {formatRelativeTime(lastUpdated)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by Job ID, Customer, or Address..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Sort:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                >
                                    <option value="completed_date">Recently Completed</option>
                                    <option value="delivery_date">Scheduled Delivery Date</option>
                                    <option value="newest">Job Created Date (Newest)</option>
                                    <option value="oldest">Job Created Date (Oldest)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredDeliveries.length}</span> of{' '}
                            <span className="font-semibold text-gray-900">{deliveries.length}</span> completed deliveries
                        </span>
                    </div>
                </div>

                {/* Deliveries List */}
                <div className="space-y-3">
                    {filteredDeliveries.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiCheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No completed deliveries found</h3>
                            <p className="text-gray-600">Try adjusting your search criteria or complete some deliveries first</p>
                        </div>
                    ) : (
                        filteredDeliveries.map((quoteCard) => {
                            const quoteType = quoteCard.quote_info?.quote_type;
                            const childUniqueId = quoteCard.quote_info?.childUniqueId;
                            const quoteStyle = getQuoteTypeStyle(quoteType);
                            const isCompleted = isQuoteDeliveryCompleted(quoteCard);
                            const deliveryStatus = quoteCard.delivery?.delivery_status || 'scheduled';

                            return (
                                <div
                                    key={quoteCard.quote_info?.id || `${quoteCard.id}-${quoteType}-${quoteCard.delivery?.id}`}
                                    onClick={() => handleViewDelivery(quoteCard.id)}
                                    className={`bg-white rounded-lg shadow-sm border transition-all duration-200 cursor-pointer ${isCompleted
                                        ? 'border-green-300 bg-green-50 hover:shadow-md hover:border-green-400'
                                        : 'border-gray-200 hover:shadow-md hover:border-blue-300'
                                        }`}
                                >
                                    <div className="p-4">
                                        {/* Header Row */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-semibold text-gray-900 text-base">
                                                        {quoteCard.unique_id}
                                                    </h3>
                                                    {/* Quote / Child ID Badge */}
                                                    <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${quoteStyle.bgColor} ${quoteStyle.textColor}`}>
                                                        {getQuoteTypeIcon(quoteType)}
                                                        {childUniqueId
                                                            ? childUniqueId
                                                            : quoteType.charAt(0).toUpperCase() + quoteType.slice(1)
                                                        }
                                                    </span>
                                                    {(quoteCard.urgent_quote || quoteCard.urgent_design) && (
                                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                                                            <FiFlag className="w-3 h-3" />
                                                            URGENT
                                                        </span>
                                                    )}
                                                    {isCompleted && (
                                                        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold flex items-center gap-1">
                                                            <FiCheckCircle className="w-3 h-3" />
                                                            Completed
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <span className={`px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getDeliveryStatusColor(deliveryStatus)}`}>
                                                {getDeliveryStatusIcon(deliveryStatus)}
                                                {deliveryStatus.replace(/_/g, ' ').toUpperCase()}
                                            </span>
                                        </div>

                                        {/* Content Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                            {/* Customer Info */}
                                            <div className="md:col-span-3">
                                                <div className="flex items-start gap-2">
                                                    <FiUser className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {quoteCard.customer_name}
                                                        </p>
                                                        <div className="flex items-center gap-1.5 mt-1">
                                                            {quoteCard.customer_number && (
                                                                <a
                                                                    href={`tel:${quoteCard.customer_number}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                                                >
                                                                    <FiPhone className="w-3 h-3" />
                                                                    Call
                                                                </a>
                                                            )}
                                                            {quoteCard.customer_mail && (
                                                                <a
                                                                    href={`mailto:${quoteCard.customer_mail}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                                >
                                                                    <FiMail className="w-3 h-3" />
                                                                    Email
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Delivery Address */}
                                            <div className="md:col-span-4">
                                                <div className="flex items-start gap-2">
                                                    <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {quoteCard.delivery?.delivery_address || quoteCard.customer_address || "No address provided"}
                                                        </p>
                                                        {(quoteCard.delivery?.delivery_address || quoteCard.customer_address) && (
                                                            <a
                                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(quoteCard.delivery?.delivery_address || quoteCard.customer_address)}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                            >
                                                                <FiMapPin className="w-3 h-3" />
                                                                Open in Maps
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Completed Date */}
                                            <div className="md:col-span-2">
                                                <div className="flex items-start gap-2">
                                                    <FiCheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-gray-500">Completed</p>
                                                        <p className="text-sm font-medium text-green-700">
                                                            {formatDate(quoteCard.delivery?.delivered_at || quoteCard.quote_info?.expected_delivery_date)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="md:col-span-3 flex items-center justify-end">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewDelivery(quoteCard.id);
                                                    }}
                                                    className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center gap-2"
                                                >
                                                    <FiEye className="w-4 h-4" />
                                                    View Details
                                                </button>
                                            </div>
                                        </div>

                                        {/* Delivery Information Section */}
                                        {(quoteCard.quote_info?.delivery_time_slot || quoteCard.quote_info?.delivery_note || quoteCard.delivery?.special_instructions || quoteCard.delivery?.delivery_notes) && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {/* Time Slot */}
                                                    {quoteCard.quote_info?.delivery_time_slot && (
                                                        <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                                            <FiClock className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-purple-900 mb-0.5">Delivery Time Slot</p>
                                                                <p className="text-sm font-semibold text-purple-700">
                                                                    {quoteCard.quote_info.delivery_time_slot} Delivery
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Delivery Note */}
                                                    {quoteCard.quote_info?.delivery_note && (
                                                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                            <FiEdit3 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-blue-900 mb-0.5">Delivery Note</p>
                                                                <p className="text-sm text-blue-700 whitespace-pre-wrap">
                                                                    {quoteCard.quote_info.delivery_note}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Special Instructions from delivery table */}
                                                    {quoteCard.delivery?.special_instructions && (
                                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg md:col-span-2">
                                                            <FiInfo className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-amber-900 mb-0.5">Special Instructions</p>
                                                                <p className="text-sm text-amber-700 whitespace-pre-wrap">
                                                                    {quoteCard.delivery.special_instructions}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Delivery Notes (driver's notes after completion) */}
                                                    {quoteCard.delivery?.delivery_notes && (
                                                        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg md:col-span-2">
                                                            <FiCheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-green-900 mb-0.5">Completion Notes</p>
                                                                <p className="text-sm text-green-700 whitespace-pre-wrap">
                                                                    {quoteCard.delivery.delivery_notes}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyCompletedDeliveries;
